import { useTranslation } from 'react-i18next';

import { Font, View } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { ChartPdfImagesGroup } from 'PDF/components/Charts/ChartPdfImagesGroup';
import { IImageChart } from 'PDF/components/Charts/types';
import { FeaturesTableWrapper, IFeaturesTableRowItem } from 'PDF/components/FeaturesTable';
import { InfoTable } from 'PDF/components/InfoTable';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { IPointTableProps, PointTableWrapper } from 'PDF/components/PointTable';
import { StepsList } from 'PDF/components/StepsList';
import { IStripedTableRow, StripedTableWrapper } from 'PDF/components/StripedTable';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { IInfoTableRow, prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import {
  getMarketingAnalysisChapterNames,
  MarketAnalysisKeys,
  MarketAnalysisSectionAvailability,
} from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { IMarketTrendsForm } from 'components/BusinessToolForm/Sections/MarketAnalysis/MarketAnalysisOverview/utils/form';

import { SectionCoverPagePdf } from '../SectionCoverPage';
import { getIsCompetitorsEmpty, getIsMarketTrendsEmpty, getIsStrengthAndWeaknessEmpty } from './mapping';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface IMarketAnalysisPdfProps {
  magazineItems: ITextItem[][];
  data: {
    marketTrends: IMarketTrendsForm[];
    consumptionForecastImages?: IImageChart[];
    structureDetailsImages?: IImageChart[];
    priceOverviewChartImage: IImageChart;
    competitorsComparison: IStripedTableRow[];
    strengthAndWeakness: IPointTableProps;
    productComparisonArray: IFeaturesTableRowItem[][];
    B2C: IInfoTableRow[];
    B2B: IInfoTableRow[];
  };
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const MarketAnalysisPdf = (
  props: IMarketAnalysisPdfProps & {
    availability: MarketAnalysisSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage,
  } = props;
  const { t } = useTranslation();
  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.MARKET_ANALYSIS, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const MARKET_ANALYSIS_CHAPTER_NAMES = getMarketingAnalysisChapterNames(isExamplePage);
  const subChapterNames = (Object.keys(availability) as MarketAnalysisKeys[])
    .filter((key) => !!availability[key])
    .map((key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${MARKET_ANALYSIS_CHAPTER_NAMES[key]}`);
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.MARKET_ANALYSIS)}
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 0, variant: 'before' }}
        subChapterNames={subChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const {
    marketTrends,
    consumptionForecastImages,
    structureDetailsImages,
    priceOverviewChartImage,
    competitorsComparison,
    strengthAndWeakness,
    productComparisonArray,
    B2C,
    B2B,
  } = data;

  const isConsumptionForecastEmpty =
    !consumptionForecastImages ||
    consumptionForecastImages.length === 0 ||
    (consumptionForecastImages.length >= 1 && consumptionForecastImages.every((image) => image.src === 'emptyChart'));
  const isStructureImagesEmpty =
    !structureDetailsImages ||
    structureDetailsImages.length === 0 ||
    (structureDetailsImages.length === 1 && structureDetailsImages[0].src === 'emptyChart');
  const isPriceOverviewEmpty = !priceOverviewChartImage || priceOverviewChartImage.src === 'emptyChart';
  const isCompetitorsComparisonEmpty = getIsCompetitorsEmpty(competitorsComparison);
  const isProductComparisonEmpty =
    productComparisonArray.length === 0 ||
    (productComparisonArray.length === 1 &&
      productComparisonArray[0].length === 2 &&
      productComparisonArray[0][0].items.every((item) =>
        typeof item === 'string' ? item === '' : item.imgSrc === '',
      ) &&
      productComparisonArray[0][1].items.every((item) =>
        typeof item !== 'string' ? item.imgSrc === '' : item === '',
      ));
  const isStrengthAndWeaknessEmpty = getIsStrengthAndWeaknessEmpty(strengthAndWeakness);
  const isMarketTrendsEmpty = getIsMarketTrendsEmpty(marketTrends);

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.MARKET_ANALYSIS)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 0, variant: 'before' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.marketOverview && (
            <>
              <SubChapterHeading
                id="marketOverview"
                heading={getSubHeading(MARKET_ANALYSIS_CHAPTER_NAMES.marketOverview)}
              />
              <PdfContentText
                items={magazineItems[0]}
                withoutBottomMargin={isConsumptionForecastEmpty && !!magazineItems[1][0].text}
              />
              {!isConsumptionForecastEmpty && (
                <ChartPdfImagesGroup
                  heading={
                    isExamplePage ? 'Consumption Forecast' : t('pdfPages.mappings.marketAnalysis.consumptionForecast')
                  }
                  images={consumptionForecastImages}
                />
              )}
              <PdfContentText items={magazineItems[1]} withoutBottomMargin={isMarketTrendsEmpty} />
              {isInvestor && !isStructureImagesEmpty && (
                <ChartPdfImagesGroup
                  heading={isExamplePage ? 'Structure Details' : t('pdfPages.mappings.marketAnalysis.structureDetails')}
                  images={structureDetailsImages}
                />
              )}
              <StepsList
                heading={isExamplePage ? 'Trend Analysis' : t('pdfPages.mappings.marketAnalysis.trendAnalysis')}
                list={marketTrends}
              />
              <PdfContentText items={magazineItems[2]} withoutBottomMargin={!isInvestor && isStructureImagesEmpty} />
              {!isInvestor && !isStructureImagesEmpty && (
                <ChartPdfImagesGroup
                  heading={isExamplePage ? 'Structure Details' : t('pdfPages.mappings.marketAnalysis.structureDetails')}
                  images={structureDetailsImages}
                />
              )}
              {!isInvestor && <PdfContentText items={magazineItems[3]} />}

              {!isPriceOverviewEmpty && (
                <ChartPdfImagesGroup
                  heading={isExamplePage ? 'Price Overview' : t('pdfPages.mappings.marketAnalysis.priceOverview')}
                  images={[priceOverviewChartImage]}
                />
              )}
            </>
          )}
          {availability.competitiveAnalysis && (
            <>
              <SubChapterHeading
                id="competitiveAnalysis"
                heading={getSubHeading(MARKET_ANALYSIS_CHAPTER_NAMES.competitiveAnalysis)}
              />
              {isInvestor && (
                <PdfContentText
                  items={magazineItems[3]}
                  withoutBottomMargin={isCompetitorsComparisonEmpty && isStrengthAndWeaknessEmpty}
                />
              )}
              {!isInvestor && (
                <PdfContentText
                  items={magazineItems[4]}
                  withoutBottomMargin={isCompetitorsComparisonEmpty && isStrengthAndWeaknessEmpty}
                />
              )}

              {!isCompetitorsComparisonEmpty && (
                <StripedTableWrapper
                  heading={
                    isExamplePage ? 'Competitor Comparison' : t('pdfPages.mappings.marketAnalysis.competitorComparison')
                  }
                  rows={competitorsComparison}
                  fixedRows={[0]}
                />
              )}
              <PointTableWrapper
                heading={
                  isExamplePage
                    ? 'Strengths and Weaknesses'
                    : t('pdfPages.mappings.marketAnalysis.strengthsAndWeaknesses')
                }
                {...strengthAndWeakness}
              />
              {!isInvestor && (
                <PdfContentText items={magazineItems[5]} withoutBottomMargin={isProductComparisonEmpty} />
              )}
              {productComparisonArray.map((rows, index) => (
                <>
                  <View
                    minPresenceAhead={
                      rows.some((row) => row.items.some((feature) => typeof feature !== 'string' && feature.imgSrc))
                        ? 200
                        : 100
                    }
                  />
                  <FeaturesTableWrapper
                    key={index}
                    heading={
                      index === 0
                        ? isExamplePage
                          ? 'Product Comparison'
                          : t('pdfPages.mappings.marketAnalysis.productComparison')
                        : ''
                    }
                    rows={rows}
                  />
                </>
              ))}
              {isInvestor && <PdfContentText items={magazineItems[4]} />}
              <PdfContentText items={magazineItems[6]} />
            </>
          )}
          {availability.consumerNeeds && (
            <>
              <View wrap={false}>
                <SubChapterHeading
                  id="consumerNeeds"
                  heading={getSubHeading(MARKET_ANALYSIS_CHAPTER_NAMES.consumerNeeds)}
                />
                <InfoTable
                  heading={isExamplePage ? 'B2C Profile' : t('pdfPages.mappings.marketAnalysis.b2cProfile')}
                  rows={[B2C]}
                />
              </View>
              <InfoTable
                heading={isExamplePage ? 'B2B Profile' : t('pdfPages.mappings.marketAnalysis.b2bProfile')}
                rows={[B2B]}
              />
              {isInvestor && <PdfContentText items={magazineItems[5]} />}
              {!isInvestor && <PdfContentText items={magazineItems[7]} />}
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
